<script setup>
import { computed, ref, toRefs } from 'vue'
import {
  BadgeCheck,
  Bell,
  Cog,
  CreditCard,
  LogOut,
} from 'lucide-vue-next'
import MentorSearchBar from './MentorSearchBar.vue'

import { Badge } from '@/components/ui/badge'
import { isLocalhost } from '@/lib/utils/devUtils.js'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

// Import the search component

// Import supabase client
import { signOutSupa } from '@/lib/supabaseClient'

// Props from parent
const props = defineProps(['session'])
const { session } = toRefs(props)

// User data
const user = computed(() => ({
  email: session.value?.user?.email || '',
  avatar: session.value?.user?.user_metadata?.avatar_url || '',
  name:
    session.value?.user?.user_metadata?.full_name
    || session.value?.user?.email?.split('@')[0]
    || '',
}))

// User menu items
const userMenuItems = [
  {
    title: 'Compte',
    icon: BadgeCheck,
    action: 'link',
    url: '/account',
  },
  {
    title: 'Facturation',
    icon: CreditCard,
    action: 'link',
    url: '/billing',
  },
  {
    title: 'Paramètres',
    icon: Cog,
    action: 'link',
    url: '/settings',
  },
  {
    title: 'Notifications',
    icon: Bell,
    action: 'link',
    url: '/notifications',
  },
  {
    type: 'separator',
  },
  {
    title: 'Déconnexion',
    icon: LogOut,
    action: 'function',
    handler: signOut,
  },
]

// Notification system
const hasUnreadNotifications = ref(true)
const notificationCount = ref(3)
const notificationOpen = ref(false)

// Sample notifications
const notifications = ref([
  {
    id: 1,
    title: 'Nouveau rendez-vous',
    message: 'Marie Dupont a réservé un rendez-vous pour le 15 mars',
    time: 'Il y a 10 minutes',
    read: false,
    type: 'appointment',
  },
  {
    id: 2,
    title: 'Commentaire reçu',
    message: 'Jean Martin a laissé un commentaire sur votre article',
    time: 'Il y a 2 heures',
    read: false,
    type: 'comment',
  },
  {
    id: 3,
    title: 'Paiement reçu',
    message: 'Paiement de 150$ reçu de Sophie Moreau',
    time: 'Hier, 15:42',
    read: false,
    type: 'payment',
  },
  {
    id: 4,
    title: 'Rappel de rendez-vous',
    message: 'Vous avez un rendez-vous avec Thomas Bernard demain à 14h',
    time: 'Hier, 09:30',
    read: true,
    type: 'reminder',
  },
  {
    id: 5,
    title: 'Mise à jour système',
    message: 'Nouvelles fonctionnalités disponibles dans votre espace',
    time: 'Il y a 3 jours',
    read: true,
    type: 'system',
  },
])

// Notification functions
function markNotificationAsRead(notif) {
  const notification = notifications.value.find(n => n.id === notif.id)
  if (notification) {
    notification.read = true
  }
  updateNotificationCount()
}

function markAllNotificationsAsRead() {
  notifications.value.forEach((notification) => {
    notification.read = true
  })
  updateNotificationCount()
}

function updateNotificationCount() {
  const unreadCount = notifications.value.filter(n => !n.read).length
  notificationCount.value = unreadCount
  hasUnreadNotifications.value = unreadCount > 0
}

// Sign out function
const loading = ref(false)
async function signOut() {
  try {
    loading.value = true
    await signOutSupa()
  }
  catch (error) {
    console.log(error)
  }
  finally {
    loading.value = false
  }
}

// Initialize
onMounted(() => {
  updateNotificationCount()
})
</script>

<template>
  <header
    class="relative z-50 flex h-16 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
  >
    <div class="container flex items-center justify-between h-full px-4">
      <!-- Search Component -->
      <div class="flex-1 flex items-center">
        <MentorSearchBar v-if="isLocalhost" />
      </div>

      <!-- Right side controls: Notifications and User Profile -->
      <div class="flex items-center gap-4">
        <!-- Notifications with Popover -->
        <Popover v-if="isLocalhost" v-model:open="notificationOpen">
          <PopoverTrigger as-child>
            <button
              class="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-accent"
            >
              <Bell class="w-5 h-5" />
              <Badge
                v-if="hasUnreadNotifications"
                class="absolute -top-1 -right-1 px-1 min-w-5 h-5 flex items-center justify-center"
              >
                {{ notificationCount }}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent class="w-80 p-0" align="end">
            <div class="flex items-center justify-between p-3 border-b">
              <span class="font-medium">Notifications</span>
              <button
                class="text-xs text-primary hover:underline"
                @click="markAllNotificationsAsRead"
              >
                Tout marquer comme lu
              </button>
            </div>
            <div class="max-h-80 overflow-y-auto">
              <div
                v-for="notification in notifications"
                :key="notification.id"
                class="relative"
              >
                <div
                  class="flex flex-col items-start gap-1 cursor-pointer p-3"
                  :class="{ 'bg-accent/50': !notification.read }"
                  @click="markNotificationAsRead(notification)"
                >
                  <div class="flex items-center justify-between w-full">
                    <span class="font-medium">{{ notification.title }}</span>
                    <span class="text-xs text-muted-foreground">{{
                      notification.time
                    }}</span>
                  </div>
                  <p class="text-sm text-muted-foreground">
                    {{ notification.message }}
                  </p>
                  <div
                    v-if="!notification.read"
                    class="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary"
                  />
                </div>
                <div
                  v-if="
                    notification.id
                      !== notifications[notifications.length - 1].id
                  "
                  class="border-t mx-3"
                />
              </div>
            </div>
            <div class="p-3 border-t">
              <RouterLink
                to="/notifications"
                class="flex justify-center w-full text-sm font-medium"
              >
                Voir toutes les notifications
              </RouterLink>
            </div>
          </PopoverContent>
        </Popover>

        <UserDropDownMenu
          pop-direction="down"
          :session="session"
          :notifications-count="5"
        />
      </div>
    </div>
  </header>
</template>
